import { z } from 'zod';
import { createServiceHttpClient } from '@wit-support/core-ports';
// Contract: app-service product-usage endpoint (service-to-service).
// Auth: `Authorization: Bearer <APP_API_KEY>` (static shared secret, NOT a user JWT).
// `GET /internal/user/{userId}/usage`
//   Success: 200 `{ "success": "success", "data": { userId, registeredAt, messages, generations } }`
//            where `registeredAt` is an ISO-8601 string or null, `messages` is `{ sent }`, and
//            `generations` is `{ compatibility, palm, portraits }` with each `{ total, completed }`.
// Errors:  400 (invalid/missing userId), 404 (user not found), 401/403 (auth).
// Parsing is tolerant of extra fields and only requires the fields we surface; any non-2xx
// (including 404), network/abort/parse error → fail soft (null).
const GenerationSchema = z.object({
    total: z.number(),
    completed: z.number(),
});
const UsageResponseSchema = z.object({
    success: z.unknown(),
    data: z.object({
        userId: z.string(),
        registeredAt: z.string().nullable(),
        messages: z.object({ sent: z.number() }),
        generations: z.object({
            compatibility: GenerationSchema,
            palm: GenerationSchema,
            portraits: GenerationSchema,
        }),
    }),
});
export const createHttpUsagePort = (opts) => {
    const client = createServiceHttpClient({
        baseUrl: opts.baseUrl,
        ...(opts.apiKey !== undefined && { apiKey: opts.apiKey }),
        ...(opts.fetchFn !== undefined && { fetchFn: opts.fetchFn }),
        ...(opts.timeoutMs !== undefined && { timeoutMs: opts.timeoutMs }),
    });
    return {
        async getUsage(userId) {
            try {
                const res = await client.get(`/internal/user/${encodeURIComponent(userId)}/usage`);
                if (!res.ok)
                    return null;
                const parsed = UsageResponseSchema.safeParse(await res.json());
                if (!parsed.success)
                    return null;
                const d = parsed.data.data;
                return {
                    userId: d.userId,
                    registeredAt: d.registeredAt,
                    messages: { sent: d.messages.sent },
                    generations: {
                        compatibility: d.generations.compatibility,
                        palm: d.generations.palm,
                        portraits: d.generations.portraits,
                    },
                };
            }
            catch {
                // Network error / AbortError (timeout) → fail soft, mirroring the !res.ok path.
                return null;
            }
        },
    };
};
//# sourceMappingURL=usage.js.map